import http from "@/core/global/http"
import { normalizeScannerPatientListResponse } from "@/core/model/integrations"

import type {
  PatientLoadTriggerResponse,
  RefreshPatientsResponse,
  ScannerFilesLoadResponse,
  ScannerFilesMode,
  ScannerPatientListQueryParams,
} from "./integrations.type"
import { buildScannerPatientListBody } from "./integrations.util"
import {
  ALL_SCANNER_INTEGRATION_IDS,
  formatScannerHeaderValue,
} from "./scannerConstants"

// Auth based endpoints

export const getScannerRedirectUrlApi = async (
  org_id: string,
  scanner: string,
) => {
  const { data } = await http.get(
    `/shared/v1/org/${org_id}/scanner/redirect-url`,
    { headers: { Scanner: formatScannerHeaderValue(scanner) } },
  )
  if (data?.status?.toLowerCase() === "success" && data.result?.redirect_url) {
    return data.result.redirect_url
  }
  throw new Error(data.message?.message ?? "Failed to get redirect URL")
}

export const getScannerConnectionStatusApi = async (org_id: string) => {
  const { data } = await http.get(
    `/shared/v1/org/${org_id}/scanner/connection-status`,
    {
      headers: {
        Scanner: formatScannerHeaderValue(ALL_SCANNER_INTEGRATION_IDS),
      },
    },
  )

  if (data?.status?.toLowerCase() === "success" && data.result) {
    return {
      org_id: data.result.org_id,
      connections: data.result.connections ?? [],
    }
  }
  throw new Error(data.message?.message ?? "Failed to get connection status")
}

export const disconnectScannerApi = async (org_id: string, scanner: string) => {
  const { data } = await http.post(
    `/shared/v1/org/${org_id}/scanner/disconnect`,
    undefined,
    { headers: { Scanner: formatScannerHeaderValue(scanner) } },
  )

  if (
    data?.status?.toLowerCase() === "success" &&
    data.result?.disconnected === true
  ) {
    return true
  }
  throw new Error(data.message?.message ?? "Failed to disconnect")
}

// Scanner patient list endpoints

export const triggerPatientListFetchApi = async (
  org_id: string,
  scannerName: string,
) => {
  return http.post<PatientLoadTriggerResponse>(
    `/shared/v1/org/${org_id}/scanner/patients/load`,
    undefined,
    {
      headers: {
        Scanner: formatScannerHeaderValue(scannerName),
      },
    },
  )
}

export const fetchScannerPatientListApi = async (
  org_id: string,
  params: ScannerPatientListQueryParams,
  scannerName: string,
) => {
  const { data } = await http.post(
    `/shared/v1/org/${org_id}/scanner/patients`,
    buildScannerPatientListBody(params),
    { headers: { Scanner: formatScannerHeaderValue(scannerName) } },
  )
  return normalizeScannerPatientListResponse(data)
}

export const refreshScannerPatientsApi = async (
  org_id: string,
  scannerName: string,
) => {
  return http.post<RefreshPatientsResponse>(
    `/shared/v1/org/${org_id}/scanner/patients/refresh`,
    undefined,
    { headers: { Scanner: formatScannerHeaderValue(scannerName) } },
  )
}

// Scanner attachment endpoints (case-level) — legacy

/** @deprecated Use `loadScannerFilesApi` instead */
export const fetchScannerAttachments = (
  org_id: string,
  caseId: string | undefined,
  scannerName: string,
) => {
  return http.get(`/shared/v1/org/${org_id}/scanner/${caseId}/attachments`, {
    headers: { Scanner: formatScannerHeaderValue(scannerName) },
  })
}

/** @deprecated Use `downloadScannerFileFromUrlApi` instead */
export const downloadScannerAttachment = async (
  org_id: string,
  caseId: string | undefined,
  attachmentId: number,
  fileName: string,
  scannerName: string,
) => {
  const res = await http.get(
    `/shared/v1/org/${org_id}/scanner/${caseId}/attachments/${attachmentId}`,
    {
      responseType: "blob",
      headers: { Scanner: formatScannerHeaderValue(scannerName) },
    },
  )
  return new File([res.data], fileName)
}

// New scanner /files/load endpoints

/** POST /shared/v1/org/{org_id}/scanner/files/load */
export const loadScannerFilesApi = async (
  org_id: string,
  externalCaseId: string | number,
  scannerName: string,
  mode: ScannerFilesMode = "temporary",
): Promise<ScannerFilesLoadResponse> => {
  const { data } = await http.post(
    `/shared/v1/org/${org_id}/scanner/files/load`,
    { external_case_id: String(externalCaseId), mode },
    { headers: { Scanner: formatScannerHeaderValue(scannerName) } },
  )
  return data as ScannerFilesLoadResponse
}

/** Download a pre-signed file URL — same blob → File pattern as `downloadScannerAttachment`. */
export const downloadScannerFileFromUrlApi = async (
  url: string,
  fileName: string,
  scannerName: string,
  signal?: AbortSignal,
): Promise<File> => {
  // update when BE sends secure URLs, but for now ensure we use https to avoid CORS issues in the browser
  const secureUrl = url.replace(/^http:\/\//, "https://")
  const res = await http.get(secureUrl, {
    responseType: "blob",
    signal,
    headers: { Scanner: formatScannerHeaderValue(scannerName) },
  })
  return new File([res.data], fileName)
}
